import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/shared/PageHeader';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { cn } from '@/lib/utils';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import {
    ToggleLeft, Plus, Pencil, RefreshCw, Loader2, X, Search, Users,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

interface FeatureFlag {
    id: string;
    key: string;
    description: string | null;
    is_enabled: boolean;
    rollout_percentage: number;
    test_user_ids: string[];
    created_at: string;
    updated_at: string;
}

interface TestUser {
    user_id: string;
    display_name: string | null;
    email: string | null;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function FeatureFlagsPage() {
    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState('');
    const [editDialog, setEditDialog] = useState<{ open: boolean; flag: FeatureFlag | null }>({ open: false, flag: null });
    const [form, setForm] = useState<{ id?: string; key: string; description: string; is_enabled: boolean; rollout_percentage: number; test_users: TestUser[] }>({ key: '', description: '', is_enabled: false, rollout_percentage: 100, test_users: [] });
    const [userSearch, setUserSearch] = useState('');
    const [showUserSearch, setShowUserSearch] = useState(false);
    const userSearchRef = useRef<HTMLDivElement>(null);
    const [deleteFlagId, setDeleteFlagId] = useState<string | null>(null);

    // ─── Load Flags ────────────────────────────
    const { data: flags = [], isLoading } = useQuery({
        queryKey: ['admin', 'feature-flags'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<FeatureFlag[]>('admin_get_feature_flags');
            if (error) throw error;
            return normalizeRpcResponse(data);
        },
    });

    const filteredFlags = searchQuery
        ? flags.filter(f =>
            f.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
            f.description?.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : flags;

    // ─── Mutations ─────────────────────────────
    const toggleMutation = useMutation({
        mutationFn: async (params: { id: string; is_enabled: boolean }) => {
            const { error } = await dbClient.rpc('admin_toggle_feature_flag', {
                p_flag_id: params.id,
                p_is_enabled: params.is_enabled,
            });
            if (error) throw error;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] }),
    });

    const saveMutation = useMutation({
        mutationFn: async (data: typeof form & { id?: string }) => {
            const params: Record<string, unknown> = {
                p_key: data.key,
                p_description: data.description || null,
                p_is_enabled: data.is_enabled,
                p_rollout_percentage: data.rollout_percentage,
                p_test_user_ids: data.test_users?.map(u => u.user_id) ?? [],
            };
            if (data.id) params.p_flag_id = data.id;

            const rpcName = data.id ? 'admin_update_feature_flag' : 'admin_create_feature_flag';
            const { error } = await dbClient.rpc(rpcName, params);
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
            setEditDialog({ open: false, flag: null });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            const { error } = await dbClient.rpc('admin_delete_feature_flag', { p_flag_id: id });
            if (error) throw error;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] }),
    });

    const openDialog = (flag: FeatureFlag | null) => {
        const ids = flag && Array.isArray(flag.test_user_ids) ? flag.test_user_ids : [];
        setForm(flag
            ? {
                id: flag.id,
                key: flag.key,
                description: flag.description || '',
                is_enabled: flag.is_enabled,
                rollout_percentage: flag.rollout_percentage,
                test_users: ids.map(id => ({ user_id: id, display_name: null, email: null })),
            }
            : { key: '', description: '', is_enabled: false, rollout_percentage: 100, test_users: [] }
        );
        setUserSearch('');
        setShowUserSearch(false);
        setEditDialog({ open: true, flag });
    };

    // ─── User search (igual que en Notificaciones) ──────────
    const { data: searchResults = [], isLoading: searchLoading } = useQuery({
        queryKey: ['admin', 'search-users-flags', userSearch],
        queryFn: async () => {
            if (!userSearch || userSearch.trim().length < 2) return [];
            const { data, error } = await dbClient.rpc<TestUser[]>('admin_search_users', {
                p_search_query: userSearch.trim(),
                p_search_by_email: true,
                p_result_limit: 10,
            });
            if (error) throw error;
            return (normalizeRpcResponse(data) as TestUser[]).map(u => ({
                user_id: u.user_id,
                display_name: u.display_name ?? null,
                email: u.email ?? null,
            }));
        },
        enabled: userSearch.trim().length >= 2 && editDialog.open,
        staleTime: 30_000,
    });

    // ─── Cargar display_name/email para usuarios existentes al abrir ──────────
    const idsToFetch = editDialog.open && editDialog.flag
        ? (editDialog.flag.test_user_ids || []).filter(Boolean)
        : [];
    const { data: batchProfiles = [] } = useQuery({
        queryKey: ['admin', 'profiles-batch-flags', idsToFetch.join(',')],
        queryFn: async () => {
            if (idsToFetch.length === 0) return [];
            const { data, error } = await dbClient.rpc<TestUser[]>('admin_get_user_profiles_batch', {
                p_user_ids: idsToFetch,
            });
            if (error) return [];
            return normalizeRpcResponse(data) as TestUser[];
        },
        enabled: editDialog.open && idsToFetch.length > 0,
        staleTime: 60_000,
    });

    useEffect(() => {
        if (editDialog.open && batchProfiles.length > 0 && form.test_users.some(u => !u.display_name && !u.email)) {
            const byId = Object.fromEntries(batchProfiles.map(p => [p.user_id, p]));
            setForm(prev => ({
                ...prev,
                test_users: prev.test_users.map(u =>
                    byId[u.user_id]
                        ? { user_id: u.user_id, display_name: byId[u.user_id].display_name, email: byId[u.user_id].email }
                        : u
                ),
            }));
        }
    }, [batchProfiles, editDialog.open]);

    // Cerrar dropdown al hacer clic fuera
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (userSearchRef.current && !userSearchRef.current.contains(e.target as Node)) {
                setShowUserSearch(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const addTestUser = (u: TestUser) => {
        if (form.test_users.some(t => t.user_id === u.user_id)) return;
        setForm(prev => ({ ...prev, test_users: [...prev.test_users, u] }));
        setUserSearch('');
        setShowUserSearch(false);
    };

    const removeTestUser = (userId: string) => {
        setForm(prev => ({ ...prev, test_users: prev.test_users.filter(t => t.user_id !== userId) }));
    };

    const filteredSearchResults = searchResults.filter(u => !form.test_users.some(t => t.user_id === u.user_id));

    const enabledCount = flags.filter(f => f.is_enabled).length;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Feature Flags"
                description={`${flags.length} flags configuradas · ${enabledCount} activas`}
            />

            {/* Toolbar */}
            <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Buscar flags..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                </div>
                <button
                    onClick={() => openDialog(null)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                    <Plus size={14} />
                    Nueva Flag
                </button>
            </div>

            {/* Flags List */}
            {isLoading ? (
                <div className="py-12 flex items-center justify-center">
                    <RefreshCw size={18} className="animate-spin text-muted-foreground" />
                </div>
            ) : filteredFlags.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                    {searchQuery ? 'No se encontraron flags.' : 'No hay feature flags configuradas.'}
                </div>
            ) : (
                <div className="space-y-2">
                    {filteredFlags.map(flag => (
                        <div
                            key={flag.id}
                            className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:border-border/80 transition-colors group"
                        >
                            {/* Toggle */}
                            <button
                                onClick={() => toggleMutation.mutate({ id: flag.id, is_enabled: !flag.is_enabled })}
                                className={cn(
                                    'w-10 h-5 rounded-full relative flex-shrink-0 transition-colors',
                                    flag.is_enabled ? 'bg-emerald-500' : 'bg-zinc-600'
                                )}
                            >
                                <span className={cn(
                                    'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all',
                                    flag.is_enabled ? 'left-5' : 'left-0.5'
                                )} />
                            </button>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-foreground font-mono">{flag.key}</span>
                                    {flag.rollout_percentage < 100 && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 font-medium">
                                            {flag.rollout_percentage}% rollout
                                        </span>
                                    )}
                                    {flag.test_user_ids?.length > 0 && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 font-medium">
                                            {flag.test_user_ids.length} testers
                                        </span>
                                    )}
                                </div>
                                {flag.description && (
                                    <p className="text-xs text-muted-foreground mt-0.5">{flag.description}</p>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => openDialog(flag)}
                                    className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                                    title="Editar configuración"
                                >
                                    <Pencil size={14} />
                                </button>
                                <button
                                    onClick={() => setDeleteFlagId(flag.id)}
                                    className="p-1.5 rounded-md hover:bg-red-500/10 transition-colors text-muted-foreground hover:text-red-400"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ─── Edit Dialog ──────────────────────────────────────────── */}
            {editDialog.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setEditDialog({ open: false, flag: null })}>
                    <div className="w-full max-w-md mx-4 rounded-xl border border-border bg-card p-6 space-y-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-foreground">
                                {editDialog.flag ? 'Editar Flag' : 'Nueva Feature Flag'}
                            </h3>
                            <button onClick={() => setEditDialog({ open: false, flag: null })} className="text-muted-foreground hover:text-foreground">
                                <X size={16} />
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="text-xs font-medium text-muted-foreground">Key *</label>
                                <input
                                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm font-mono"
                                    placeholder="enable_new_feed"
                                    value={form.key}
                                    onChange={e => setForm(p => ({ ...p, key: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-muted-foreground">Descripción</label>
                                <input
                                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                                    placeholder="Habilita el nuevo feed de noticias"
                                    value={form.description}
                                    onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-muted-foreground">Rollout %</label>
                                    <input
                                        type="number"
                                        className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                                        min={0}
                                        max={100}
                                        value={form.rollout_percentage}
                                        onChange={e => setForm(p => ({ ...p, rollout_percentage: parseInt(e.target.value) || 0 }))}
                                    />
                                </div>
                                <div className="flex items-end">
                                    <label className="flex items-center gap-2 cursor-pointer mb-2">
                                        <input
                                            type="checkbox"
                                            checked={form.is_enabled}
                                            onChange={e => setForm(p => ({ ...p, is_enabled: e.target.checked }))}
                                            className="rounded border-border w-3.5 h-3.5 accent-primary"
                                        />
                                        <span className="text-xs text-foreground">Activada</span>
                                    </label>
                                </div>
                            </div>
                            <div ref={userSearchRef}>
                                <label className="text-xs font-medium text-muted-foreground">Usuarios de prueba (solo estos verán la funcionalidad si rollout &lt; 100%)</label>
                                <div className="mt-1 space-y-2">
                                    {form.test_users.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {form.test_users.map(u => (
                                                <span
                                                    key={u.user_id}
                                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-500/15 border border-blue-500/30 text-sm"
                                                >
                                                    <Users size={12} className="text-blue-400 shrink-0" />
                                                    <span className="text-foreground truncate max-w-[140px]">
                                                        {u.display_name || u.email || `${u.user_id.slice(0, 8)}...`}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeTestUser(u.user_id)}
                                                        className="p-0.5 rounded hover:bg-blue-500/20 text-blue-400 hover:text-red-400 transition-colors"
                                                        aria-label="Quitar usuario"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="relative">
                                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background">
                                            <Search size={14} className="text-muted-foreground shrink-0" />
                                            <input
                                                className="flex-1 bg-transparent text-foreground text-sm focus:outline-none min-w-0"
                                                placeholder="Buscar usuario por nombre o email..."
                                                value={userSearch}
                                                onChange={e => {
                                                    setUserSearch(e.target.value);
                                                    setShowUserSearch(true);
                                                }}
                                                onFocus={() => setShowUserSearch(true)}
                                            />
                                            {searchLoading && <Loader2 size={14} className="animate-spin text-muted-foreground shrink-0" />}
                                        </div>
                                        {showUserSearch && filteredSearchResults.length > 0 && (
                                            <div className="absolute z-10 top-full left-0 right-0 mt-1 rounded-lg border border-border bg-card shadow-xl max-h-48 overflow-y-auto">
                                                {filteredSearchResults.map(u => (
                                                    <button
                                                        key={u.user_id}
                                                        type="button"
                                                        onClick={() => addTestUser(u)}
                                                        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                                                    >
                                                        <Users size={14} className="text-muted-foreground shrink-0" />
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium text-foreground truncate">{u.display_name || 'Sin nombre'}</p>
                                                            <p className="text-xs text-muted-foreground truncate">{u.email || u.user_id}</p>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {showUserSearch && userSearch.trim().length >= 2 && !searchLoading && filteredSearchResults.length === 0 && (
                                            <p className="absolute top-full left-0 right-0 mt-1 px-3 py-2 text-xs text-muted-foreground bg-card border border-border rounded-lg">
                                                No se encontraron usuarios
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                onClick={() => setEditDialog({ open: false, flag: null })}
                                className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => saveMutation.mutate({ ...form, id: editDialog.flag?.id })}
                                disabled={!form.key || saveMutation.isPending}
                                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                            >
                                {saveMutation.isPending ? 'Guardando...' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <AdminConfirmDialog
                open={!!deleteFlagId}
                variant="danger"
                title="Eliminar feature flag"
                description={
                    deleteFlagId ? (
                        <p>
                            ¿Eliminar el flag{' '}
                            <span className="font-mono text-foreground">
                                {flags.find((f) => f.id === deleteFlagId)?.key ?? deleteFlagId}
                            </span>
                            ?
                        </p>
                    ) : null
                }
                confirmLabel="Eliminar"
                onCancel={() => setDeleteFlagId(null)}
                onConfirm={() => {
                    if (deleteFlagId) {
                        deleteMutation.mutate(deleteFlagId);
                        setDeleteFlagId(null);
                    }
                }}
                confirmDisabled={deleteMutation.isPending}
            />
        </div>
    );
}
